import { useState } from "react";
import {
  useDlq,
  useDlqAction,
  type DlqMessage,
  type DlqStatus,
} from "../lib/dlq";
import { Button } from "../components/ui/Button";
import { EmptyState } from "../components/ui/EmptyState";
import {
  TableShell,
  Table as UITable,
  THead,
  TRow,
  TCell,
} from "../components/ui/Table";
import { Skeleton, SkeletonRow } from "../components/ui/Skeleton";

const TABS: ReadonlyArray<{ key: DlqStatus; label: string }> = [
  { key: "pending", label: "Pending" },
  { key: "retried", label: "Retried" },
  { key: "discarded", label: "Discarded" },
];

export default function Dlq() {
  const [tab, setTab] = useState<DlqStatus>("pending");
  const list = useDlq(tab);
  const retry = useDlqAction("retry");
  const discard = useDlqAction("discard");

  const messages = list.data?.messages ?? [];

  return (
    <section className="p-4 sm:p-6 lg:p-8">
      <header className="flex items-baseline justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            Dead-letter queue
          </h1>
          <p className="mt-2 text-sm text-neutral-400">
            Per-company check messages that exhausted all retries on{" "}
            <span className="font-mono text-neutral-300">resume-job-checks</span>.
            Retry re-runs the company in place against the original{" "}
            <span className="font-mono text-neutral-300">runId</span>.
          </p>
        </div>
        <span className="text-xs text-neutral-500" aria-live="polite">
          {list.data ? (
            `${messages.length} ${tab}`
          ) : list.isLoading ? (
            <Skeleton className="inline-block h-3 w-20" />
          ) : null}
        </span>
      </header>

      <div
        className="mt-6 flex gap-1 border-b border-neutral-800"
        role="tablist"
        aria-label="DLQ status"
      >
        {TABS.map((t) => (
          <button
            key={t.key}
            role="tab"
            aria-selected={tab === t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm transition ${
              tab === t.key
                ? "border-b-2 border-accent-500 text-neutral-100"
                : "border-b-2 border-transparent text-neutral-400 hover:text-neutral-100"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="mt-4">
        {list.isLoading ? (
          <TableShell>
            <UITable>
              <THead>
                <HeaderRow />
              </THead>
              <tbody>
                {Array.from({ length: 4 }).map((_, i) => (
                  <SkeletonRow key={i} cols={6} />
                ))}
              </tbody>
            </UITable>
          </TableShell>
        ) : list.error ? (
          <TableShell>
            <EmptyState
              tone="danger"
              title="Couldn't load DLQ"
              description={String(list.error)}
              action={
                <Button variant="secondary" onClick={() => list.refetch()}>
                  Retry
                </Button>
              }
            />
          </TableShell>
        ) : messages.length === 0 ? (
          <TableShell>
            <EmptyState
              title={
                tab === "pending"
                  ? "No DLQ messages — main queue is healthy."
                  : `No ${tab} messages.`
              }
            />
          </TableShell>
        ) : (
          <TableShell>
            <UITable>
              <THead>
                <HeaderRow />
              </THead>
              <tbody>
                {messages.map((m) => (
                  <Row
                    key={m.id}
                    msg={m}
                    onRetry={() => retry.mutate(m.id)}
                    onDiscard={() => discard.mutate(m.id)}
                    busy={retry.isPending || discard.isPending}
                  />
                ))}
              </tbody>
            </UITable>
          </TableShell>
        )}
      </div>
    </section>
  );
}

function HeaderRow() {
  return (
    <tr>
      <th scope="col" className="px-4 py-3 text-left font-medium">
        Company
      </th>
      <th scope="col" className="px-4 py-3 text-left font-medium">
        Run
      </th>
      <th scope="col" className="px-4 py-3 text-right font-medium">
        Attempts
      </th>
      <th scope="col" className="px-4 py-3 text-left font-medium">
        First seen
      </th>
      <th scope="col" className="px-4 py-3 text-left font-medium">
        Error
      </th>
      <th scope="col" className="px-4 py-3 text-right font-medium">
        Actions
      </th>
    </tr>
  );
}

function Row({
  msg,
  onRetry,
  onDiscard,
  busy,
}: {
  msg: DlqMessage;
  onRetry: () => void;
  onDiscard: () => void;
  busy: boolean;
}) {
  return (
    <TRow className="align-top">
      <TCell className="text-neutral-100">
        {msg.company_name ?? (
          <span className="text-neutral-500">{msg.company_id}</span>
        )}
        <div className="font-mono text-[10px] text-neutral-500">
          {msg.company_id}
        </div>
      </TCell>
      <TCell className="font-mono text-xs text-neutral-400">
        {msg.run_id ? (
          msg.run_id.slice(0, 8)
        ) : (
          <span className="text-neutral-600">—</span>
        )}
      </TCell>
      <TCell className="text-right font-mono text-neutral-300">
        {msg.attempts}
      </TCell>
      <TCell className="text-neutral-400 whitespace-nowrap">
        {new Date(msg.first_seen_at).toLocaleString(undefined, {
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        })}
      </TCell>
      <TCell className="max-w-md break-words text-danger-fg">
        {msg.error ?? <span className="text-neutral-500">—</span>}
      </TCell>
      <TCell className="text-right">
        {msg.status === "pending" ? (
          <div className="flex justify-end gap-2">
            <Button variant="primary" size="sm" disabled={busy} onClick={onRetry}>
              Retry
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={busy}
              onClick={onDiscard}
            >
              Discard
            </Button>
          </div>
        ) : (
          <span className="text-xs text-neutral-500">
            {msg.resolved_at
              ? new Date(msg.resolved_at).toLocaleString(undefined, {
                  month: "short",
                  day: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                })
              : msg.status}
          </span>
        )}
      </TCell>
    </TRow>
  );
}
